/**
 * People IQ — Bulk Upload page.
 * Parses CSV/XLSX client-side, POSTs rows to the bulk-upload endpoint, and
 * shows a per-row result summary. Auto-links Asset IQ user accounts by
 * matching official / personal email against existing users.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Download, Upload, CheckCircle2, AlertTriangle, Link2, FileSpreadsheet } from "lucide-react";

const EXPECTED_COLUMNS = [
  "full_name", "employee_code", "personal_email", "official_email",
  "personal_mobile", "official_mobile", "department", "designation",
  "date_of_joining", "date_of_birth", "gender", "marital_status",
  "blood_group", "pan", "aadhaar", "bank_account_number", "bank_ifsc",
  "bank_name", "location", "lifecycle_state",
];

export default function BulkUpload() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [autoLink, setAutoLink] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const parseFile = (file) => {
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const wb = XLSX.read(evt.target.result, { type: "binary" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        // Normalise: keep only known columns, coerce to strings, drop entirely-empty rows.
        const clean = raw
          .map((r) => {
            const out = {};
            EXPECTED_COLUMNS.forEach((c) => {
              const v = r[c] !== undefined ? r[c] : (r[c.replace(/_/g, " ")] || "");
              if (v !== "" && v !== null && v !== undefined) out[c] = String(v).trim();
            });
            return out;
          })
          .filter((r) => Object.keys(r).length > 0);
        setRows(clean);
        setFileName(file.name);
        setResult(null);
        toast.success(`Parsed ${clean.length} rows from ${file.name}`);
      } catch (e) {
        toast.error("Failed to parse file — is it a valid CSV / Excel?");
      }
    };
    reader.readAsBinaryString(file);
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("assetiq_token");
      const res = await fetch(`${api.defaults.baseURL}/people/employees/bulk-upload/template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to fetch template");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "people_iq_bulk_template.csv";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };

  // Sep 2026 — Excel template generated client-side: Employees sheet with
  // headers + sample row, Notes sheet explaining mandatory vs optional columns.
  const downloadXlsxTemplate = () => {
    const sample = [
      "Priya Sharma", "1042", "priya.personal@gmail.com", "priya@bbsglobal.in",
      "+91 98200 11111", "", "Production", "Supervisor",
      "2024-06-01", "", "", "",
      "", "", "", "", "",
      "", "Head Office", "active",
    ];
    const ws = XLSX.utils.aoa_to_sheet([EXPECTED_COLUMNS, sample]);
    ws["!cols"] = EXPECTED_COLUMNS.map((c) => ({ wch: Math.max(c.length + 2, 18) }));
    const notes = XLSX.utils.aoa_to_sheet([
      ["People IQ bulk upload — column guide"],
      [],
      ["Column", "Mandatory?", "Notes"],
      ["full_name", "YES", "Employee's full legal name"],
      ["employee_code", "YES for staff", "Numeric only. Leave blank only for pre-join candidates (code is assigned when they join)"],
      ["official_email", "One email required", "Provide official_email OR personal_email — at least one per staff row"],
      ["personal_email", "One email required", "Also used to auto-link the employee's existing Asset IQ login"],
      ["official_mobile", "One mobile required", "Provide official_mobile OR personal_mobile — at least one per staff row"],
      ["personal_mobile", "One mobile required", "Candidate rows (no code): personal_email or personal_mobile required"],
      ["department", "optional", "Matched by name — auto-created if new"],
      ["designation", "optional", "Matched by name — auto-created if new"],
      ["date_of_joining, date_of_birth", "optional", "YYYY-MM-DD"],
      ["gender, marital_status, blood_group", "optional", "Employees can complete these themselves from My Profile"],
      ["pan, aadhaar, bank_*", "optional", "Sensitive — employees can fill these themselves later via ESS"],
      ["location", "optional", ""],
      ["lifecycle_state", "optional", "active (default) or candidate"],
    ]);
    notes["!cols"] = [{ wch: 36 }, { wch: 20 }, { wch: 72 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Employees");
    XLSX.utils.book_append_sheet(wb, notes, "Notes");
    XLSX.writeFile(wb, "people_iq_bulk_template.xlsx");
    toast.success("Excel template downloaded");
  };

  const upload = async () => {
    if (rows.length === 0) return toast.error("No rows to upload");
    setUploading(true);
    try {
      const { data } = await api.post("/people/employees/bulk-upload", {
        rows,
        dry_run: dryRun,
        auto_link_users_by_email: autoLink,
      });
      setResult(data);
      if (!dryRun) toast.success(`Created ${data.created} employee(s), auto-linked ${data.linked}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="bulk-upload-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/people")}><ArrowLeft className="w-4 h-4" /></Button>
        <div>
          <div className="text-xs font-semibold tracking-widest uppercase text-[#7C1D3A]">People IQ</div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-0.5">Bulk Upload</h1>
          <p className="text-sm text-slate-500">Import existing staff from a CSV or Excel file. Auto-links Asset IQ accounts by email.</p>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">1 · Prepare your file</CardTitle></CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p className="text-slate-600">Start from the template — it has the exact column headers the system expects. You can add or remove rows freely, but keep the header row intact.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-2" /> Download CSV Template
            </Button>
            <Button variant="outline" onClick={downloadXlsxTemplate} data-testid="download-xlsx-template-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Download Excel Template (.xlsx)
            </Button>
          </div>
          <div className="text-xs text-slate-500 leading-relaxed rounded-md border border-slate-200 bg-slate-50 p-3">
            <p className="font-semibold mb-1">Required per staff row:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              <li><b>full_name</b> and <b>employee_code</b> (numeric) — always required.</li>
              <li>At least one email: <b>official_email</b> or <b>personal_email</b> — used to auto-link the employee's Asset IQ login.</li>
              <li>At least one mobile: <b>official_mobile</b> or <b>personal_mobile</b>.</li>
              <li>Everything else is optional — employees complete their own profiles later from My Profile.</li>
              <li>Leave <b>employee_code</b> blank only for pre-join <b>candidates</b> (they then need personal_email or personal_mobile; code is assigned when they join).</li>
            </ul>
            <p className="mt-2"><b>department</b> and <b>designation</b> are matched by name — new ones are auto-created if they don't already exist.</p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">2 · Upload &amp; preview</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <label className="block rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center cursor-pointer hover:bg-slate-100 transition-colors" data-testid="drop-zone">
            <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 text-slate-400" />
            <div className="text-sm text-slate-600">
              {fileName ? <><b>{fileName}</b> — <span className="text-slate-500">{rows.length} rows parsed</span></> : "Click to choose a CSV or Excel file"}
            </div>
            <input type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => e.target.files?.[0] && parseFile(e.target.files[0])} data-testid="file-input" />
          </label>

          {rows.length > 0 && (
            <>
              <div className="max-h-64 overflow-auto rounded border border-slate-200">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5 w-10">#</th>
                      <th className="text-left px-2 py-1.5">Name</th>
                      <th className="text-left px-2 py-1.5">Code</th>
                      <th className="text-left px-2 py-1.5">Email</th>
                      <th className="text-left px-2 py-1.5">Department</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0, 50).map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 text-slate-400 font-mono">{i + 1}</td>
                        <td className="px-2 py-1.5 truncate max-w-[180px]">{r.full_name || <span className="text-rose-500">MISSING</span>}</td>
                        <td className="px-2 py-1.5 font-mono">{r.employee_code || <Badge variant="outline" className="text-[9px]">candidate</Badge>}</td>
                        <td className="px-2 py-1.5 truncate max-w-[220px]">{r.official_email || r.personal_email || "—"}</td>
                        <td className="px-2 py-1.5 truncate max-w-[120px]">{r.department || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {rows.length > 50 && <div className="text-center text-xs text-slate-400 py-2">…and {rows.length - 50} more rows</div>}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={dryRun} onChange={(e) => setDryRun(e.target.checked)} data-testid="dryrun-cb" />
                  <b>Dry-run</b> (validate only, don't create records)
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={autoLink} onChange={(e) => setAutoLink(e.target.checked)} data-testid="autolink-cb" />
                  Auto-link Asset IQ accounts by email match
                </label>
                <Button
                  onClick={upload}
                  disabled={uploading}
                  className={dryRun ? "bg-slate-700 hover:bg-slate-800" : "bg-[#7C1D3A] hover:bg-[#6c1c36]"}
                  data-testid="upload-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> {uploading ? "Uploading…" : dryRun ? "Validate" : `Create ${rows.length} records`}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {result && (
        <Card data-testid="bulk-result">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              {result.errors.filter((e) => !e.warn).length === 0 ? <CheckCircle2 className="w-5 h-5 text-emerald-600" /> : <AlertTriangle className="w-5 h-5 text-amber-600" />}
              {dryRun ? "Validation result" : "Upload result"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Rows" value={result.total} tint="slate" />
              <Stat label={dryRun ? "Would create" : "Created"} value={result.created} tint="emerald" />
              <Stat label="Skipped" value={result.skipped} tint={result.skipped ? "rose" : "slate"} />
              <Stat label="Auto-linked" value={result.linked} tint="blue" icon={<Link2 className="w-3 h-3" />} />
            </div>
            {result.errors.length > 0 && (
              <div className="rounded border border-slate-200">
                <div className="bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-600">Row-level messages</div>
                <ul className="text-xs divide-y divide-slate-100">
                  {result.errors.map((e, i) => (
                    <li key={i} className="px-3 py-1.5 flex items-start gap-2">
                      <Badge className={e.warn ? "bg-amber-100 text-amber-800" : "bg-rose-100 text-rose-700"}>
                        Row {e.row}
                      </Badge>
                      <span className="text-slate-700">{e.error || e.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!dryRun && result.created > 0 && (
              <Button variant="outline" size="sm" onClick={() => navigate("/people")}>Open Employee List →</Button>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, tint, icon }) {
  const map = {
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    rose: "bg-rose-50 border-rose-200 text-rose-700",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
  };
  return (
    <div className={`rounded-lg border px-3 py-2 ${map[tint] || map.slate}`}>
      <div className="text-[10px] uppercase font-semibold tracking-wider opacity-70 flex items-center gap-1">{icon}{label}</div>
      <div className="text-2xl font-bold font-heading">{value}</div>
    </div>
  );
}
